import {
  allFormsKey,
  allHistoriesKey,
  allIntegrationsKey,
  allMsgsKey,
  allOrdersKey,
  allQuestionsKey,
  allTicketsKey,
  messageSubChannel,
  ss,
  voiceSubChannel,
} from "consts";
import { AttendanceModel, BaseProviderType } from "models";
import {
  EmailProvider,
  FormFieldProvider,
  HistoricProvider,
  IntegrationProvider,
  MessageProvider,
  OrderProvider,
  QuestionProvider,
  TicketProvider,
  useAttendList,
  useProfile,
  useSoftphone,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

type AttendDetailContextType = {
  pIdOmni: string;
  attendance?: AttendanceModel;
  isMessage?: boolean;
  isVoice?: boolean;
  clearAttendanceData: (idOmni: string) => void;
};

type AttendDetailProviderType = BaseProviderType & {
  pIdOmni: string;
};

export type AttBaseProviderType = BaseProviderType & {
  all: any;
};

export type PermissionAttProviderType = AttBaseProviderType & {
  hasPermission?: boolean;
};

const AttendDetailContext = createContext({} as AttendDetailContextType);

const allMessages = JSON.parse(ss.getItem(allMsgsKey) ?? "{}");
const allIntegrations = JSON.parse(ss.getItem(allIntegrationsKey) ?? "{}");
const allTickets = JSON.parse(ss.getItem(allTicketsKey) ?? "{}");
const allOrders = JSON.parse(ss.getItem(allOrdersKey) ?? "{}");
const allQuestions = JSON.parse(ss.getItem(allQuestionsKey) ?? "{}");
const allForms = JSON.parse(ss.getItem(allFormsKey) ?? "{}");
const allHistories = JSON.parse(ss.getItem(allHistoriesKey) ?? "{}");

export const AttendDetailProvider = ({
  children,
  pIdOmni,
}: AttendDetailProviderType) => {
  const { user } = useProfile();
  const { resetSoftphoneActions } = useSoftphone();
  const { getAttendance } = useAttendList();

  const attendance = useMemo(
    () => getAttendance(Number(pIdOmni)),
    [pIdOmni, getAttendance]
  );

  const isMessage = useMemo(
    () => attendance && messageSubChannel.includes(attendance.IdSubChannelType),
    [attendance]
  );

  const isVoice = useMemo(
    () => attendance && voiceSubChannel.includes(attendance.IdSubChannelType),
    [attendance]
  );

  useEffect(() => {
    if (pIdOmni) resetSoftphoneActions();
  }, [pIdOmni, resetSoftphoneActions]);

  const clearAttendanceData = useCallback((idOmni: string) => {
    delete allIntegrations[idOmni];
    ss.setItem(allIntegrationsKey, JSON.stringify(allIntegrations));

    delete allForms[idOmni];
    ss.setItem(allFormsKey, JSON.stringify(allForms));

    delete allHistories[idOmni];
    ss.setItem(allHistoriesKey, JSON.stringify(allHistories));

    delete allMessages[idOmni];
    ss.setItem(allMsgsKey, JSON.stringify(allMessages));

    delete allTickets[idOmni];
    ss.setItem(allTicketsKey, JSON.stringify(allTickets));

    delete allOrders[idOmni];
    ss.setItem(allOrdersKey, JSON.stringify(allOrders));

    delete allQuestions[idOmni];
    ss.setItem(allQuestionsKey, JSON.stringify(allQuestions));
  }, []);

  return (
    <AttendDetailContext.Provider
      value={{ pIdOmni, attendance, isMessage, isVoice, clearAttendanceData }}
    >
      <MessageProvider hasPermission={isMessage} all={allMessages}>
        <IntegrationProvider hasPermission={isVoice} all={allIntegrations}>
          <TicketProvider
            hasPermission={user?.hasTicketModule}
            all={allTickets}
          >
            <OrderProvider hasPermission={user?.hasOrderModule} all={allOrders}>
              <QuestionProvider
                hasPermission={user?.hasQuestionnaireModule}
                all={allQuestions}
              >
                <FormFieldProvider all={allForms}>
                  <HistoricProvider all={allHistories}>
                    <EmailProvider>{children}</EmailProvider>
                  </HistoricProvider>
                </FormFieldProvider>
              </QuestionProvider>
            </OrderProvider>
          </TicketProvider>
        </IntegrationProvider>
      </MessageProvider>
    </AttendDetailContext.Provider>
  );
};

export const useAttendDetail = () => useContext(AttendDetailContext);
